import {
  ModuleCategory,
  OptionalString,
  PublishesTo,
  StringEnum,
  SubscribesTo,
  createComponentAdmin,
  type SchemaTypeHelper,
} from '@backstage-components/base';
import {VideoSource} from '@backstage/api-types';
import {Type} from '@sinclair/typebox';

export const DEFAULT_CHROMECAST_RECEIVER_APPLICATION_ID = '272D8AB3';

export const reactName = 'Video';
export const name = reactName;
export const description = reactName;
const category: ModuleCategory = 'media';

const markerSchema = Type.Object({
  time: Type.Number({title: 'Time (in seconds, i.e. 60)'}),
  title: Type.Optional(Type.String({title: 'Title'})),
  duration: Type.Optional(
    Type.Number({title: 'Duration (in seconds, i.e. 300)'})
  ),
  imageUrl: Type.Optional(Type.String({title: 'Marker Image'})),
  cssClasses: Type.Optional(Type.Array(Type.String(), {title: ''})),
});

const schema = Type.Object(
  {
    autoplay: Type.Optional(
      Type.Boolean({
        title: 'Autoplay',
        description:
          "Start the video playback automatically, as per your device and browser's auto-play policy.",
      })
    ),
    muted: Type.Optional(
      Type.Boolean({
        title: 'Muted',
        description: 'Start the video muted.',
      })
    ),
    loop: Type.Optional(Type.Boolean({title: 'Loop'})),
    showControls: Type.Optional(
      Type.Boolean({
        title: 'Show video playback controls',
        default: true,
      })
    ),
    isOverlayEnabled: Type.Optional(
      Type.Boolean({
        title: 'Expose slots for overlays',
        default: false,
      })
    ),
    source: Type.Union(VideoSource.anyOf, {title: 'Video Source'}),
    hasWatermark: Type.Optional(
      Type.Boolean({
        title: 'Use Watermark',
        description: 'Add a watermark to the video',
        default: false,
      })
    ),
    pictureInPicture: Type.Optional(
      Type.Boolean({
        title: 'Enable Picture in Picture',
        description:
          'Allow the video to be played in Picture in Picture mode when scrolling',
        default: false,
      })
    ),
    colorSeekbarLine: Type.String({
      title: 'Seekbar Line Color',
      default: '#ffffff',
      description:
        "The color of the video's seekbar line background (applied to both timeline and volume seekbars).",
    }),
    colorAccent: Type.String({title: 'Accent Color', default: '#ffffff'}),
    chromecastReceiverId: Type.Optional(
      Type.String({
        title: 'Chromecast Receiver',
        default: DEFAULT_CHROMECAST_RECEIVER_APPLICATION_ID,
        description:
          'Specify the application id of the Chromecast Receiver to use when casting',
      })
    ),
    poster: Type.Optional(
      Type.String({
        title: 'Poster Image',
        description: 'The preview image displayed until the video starts.',
      })
    ),
    advancedOptions: Type.Object(
      {
        useCbls: Type.Optional(
          Type.Boolean({
            title: 'Use CBLS',
            description:
              'Using CBLS simulates live playback by hiding some video controls. (play button and scrub)',
          })
        ),
        markers: Type.Optional(
          Type.Array(markerSchema, {
            title: 'Timeline Markers',
            description:
              'Add markers or chapters to your playback timeline to allow viewers to navigate to a specific section of the video.',
          })
        ),
        latencyConfig: Type.Optional(
          StringEnum(['Higher Stability', 'Lower Latency'], {
            title: 'Latency Config',
            default: 'Higher Stability',
            description:
              'Configure the player to prefer playback stabilty on slower networks, or lower the latency for live content.',
          })
        ),
        newUi: Type.Optional(
          Type.Boolean({
            title: 'Use New UI',
            description: 'Use the new in-progress UI (for testing)',
          })
        ),
      },
      {
        title: 'Advanced Options',
        dependencies: {
          useCbls: {
            oneOf: [
              {
                properties: {
                  useCbls: {
                    enum: [true],
                  },
                  startTime: Type.String({
                    title: 'CBLS Start Time',
                  }),
                },
                required: ['startTime'],
              },
              {
                properties: {
                  useCbls: {
                    enum: [false],
                  },
                  startTime: Type.Optional(
                    Type.String({
                      title: 'CBLS Start Time',
                      default: undefined,
                    })
                  ),
                },
              },
            ],
          },
        },
      }
    ),
  },
  {
    dependencies: {
      hasWatermark: {
        oneOf: [
          {
            properties: {
              hasWatermark: {
                enum: [true],
              },
              watermarkOptions: Type.Object(
                {
                  watermarkTemplate: Type.Optional(
                    Type.String({
                      title: 'Watermark Template',
                      description:
                        "The template for the watermark's text. Use %name% to insert the participant's name. Use %email% to insert the participant's email.",
                      default: 'Prepared for %name%',
                    })
                  ),
                  watermarkPosition: Type.Optional(
                    Type.Object(
                      {
                        x: Type.Number({title: 'X', default: 50}),
                        y: Type.Number({title: 'Y', default: 10}),
                      },
                      {
                        title: 'Watermark Position',
                        description:
                          'The position of the watermark on the video in percentage from the top left corner.',
                      }
                    )
                  ),
                },
                {
                  title: 'Watermark Options',
                  description: 'Options for the watermark',
                }
              ),
            },
          },
        ],
      },
      autoplay: {
        oneOf: [
          {
            properties: {
              autoplay: {
                enum: [true],
              },
              advancedOptions: {
                properties: {
                  forceAutoplay: Type.Optional(
                    Type.Boolean({
                      title: 'Force Autoplay',
                      description:
                        "Typically, browsers will not autoplay videos unless the user has interacted with the video or if the video is muted by default. This option will attempt to un-mute the video's volume and force the video to autoplay.",
                    })
                  ),
                },
              },
            },
          },
        ],
      },
    },
  }
);

const uiSchema = {
  'ui:order': [
    'autoplay',
    'muted',
    'loop',
    'showControls',
    'isOverlayEnabled',
    'pictureInPicture',
    'source',
    'poster',
    'hasWatermark',
    'watermarkOptions',
    'colorSeekbarLine',
    'colorAccent',
    'advancedOptions',
    'chromecastReceiverId',
    'animationStates',
    'styleAttr',
  ],
  watermarkOptions: {
    'ui:order': ['watermarkTemplate', 'watermarkPosition'],
  },
  advancedOptions: {
    'ui:order': [
      'forceAutoplay',
      'useCbls',
      'startTime',
      'latencyConfig',
      'newUi',
      'markers',
    ],
    markers: {
      items: {
        time: {},
        title: {},
        duration: {},
        imageUrl: {
          'ui:widget': 'AssetSelectWidget',
          'ui:options': {
            buttonTitle: 'Select Asset',
          },
        },
        cssClasses: {
          'ui:options': {
            addable: false,
            removable: false,
            sortable: false,
          },
        },
      },
    },
    startTime: {
      'ui:widget': 'date-time',
    },
  },
  source: {
    'ui:field': 'VideoField',
    // hide label here, the video field used in rjsf creates its own for content management
    'ui:label': false,
    'ui:fieldReplacesAnyOrOneOf': true,
    assetId: {
      'ui:widget': 'hidden',
    },
  },
  colorAccent: {
    'ui:widget': 'color',
  },
  colorSeekbarLine: {
    'ui:widget': 'color',
  },
  poster: {
    'ui:widget': 'AssetSelectWidget',
    'ui:options': {
      buttonTitle: 'Select Asset',
    },
  },
};

const instructions = Type.Union([
  PublishesTo({
    topic: `${reactName}:ended`,
    description: 'Video playback has ended',
    options: {'$lcd-flow-ignore': true},
  }),
  PublishesTo({
    topic: `${reactName}:on-ended`,
    description: 'Video playback has started',
  }),
  PublishesTo({
    topic: `${reactName}:on-metadata`,
    description: 'Video metadata event fired',
    meta: {
      type: Type.String({
        title: 'MetadataType',
        description: 'The type of metadata emitted by the player.',
      }),
      data: Type.Unknown({
        title: 'Metadata',
        description: 'The metadata object as encountered in the stream.',
      }),
    },
  }),
  PublishesTo({
    topic: `${reactName}:on-mute`,
    description: 'Video audio has been muted (volume set to zero)',
  }),
  PublishesTo({
    topic: `${reactName}:on-play`,
    description: 'Video playback has started',
  }),
  PublishesTo({
    topic: `${reactName}:on-pause`,
    description: 'Video playback has paused',
  }),
  PublishesTo({
    topic: `${reactName}:on-unmute`,
    description: 'Video audio has been unmuted (volume set above zero)',
  }),
  PublishesTo({
    topic: `${reactName}:started`,
    description: 'Video playback has started',
    options: {'$lcd-flow-ignore': true},
  }),
  PublishesTo({
    topic: `${reactName}:on-error`,
    description: 'Video player fired an error',
    meta: {
      code: Type.Number({
        title: 'Error Code',
        description: 'The error code from bitmovin associated with the error.',
      }),
      name: Type.String({
        title: 'Error Name',
        description: 'The name from bitmovin associated with the error.',
      }),
      mesage: Type.Optional(
        Type.String({
          title: 'Error Message',
          description: 'Message provided by bitmovin.',
        })
      ),
    },
    options: {'$lcd-flow-ignore': true},
  }),
  SubscribesTo({
    topic: `${reactName}:setCbls`,
    description: 'Turn CBLS on or off',
    meta: {
      cbls: StringEnum(['true', 'false'], {
        title: 'CBLS',
        description: 'Set CBLS on or off',
      }),
    },
  }),
  SubscribesTo({
    topic: `${reactName}:setStartTime`,
    description: 'Set the start time for CBLS playback.',
    meta: {
      startTime: Type.String({
        title: 'Start Time',
        description: 'The timestamp to set.',
        format: 'date-time',
      }),
    },
  }),
  SubscribesTo({
    topic: `${reactName}:toggleControls`,
    description: 'Toggle whether playback controls are visible.',
  }),
  SubscribesTo({
    topic: `${reactName}:play`,
    description: 'Play the current video',
  }),
  SubscribesTo({
    topic: `${reactName}:pause`,
    description: 'Pause the current video',
  }),
  SubscribesTo({
    topic: `${reactName}:changeSource`,
    description: 'Change the source of the video',
    meta: {
      hls: OptionalString('HLS'),
      dash: OptionalString('DASH'),
      progressive: OptionalString('Progressive'),
    },
  }),
]);

export const ComponentDefinition = createComponentAdmin({
  id: '166a99e3-36ac-2f75-7f73-ef97e22bbfff',
  reactName,
  name,
  slug: reactName,
  description,
  version: 1,
  defaultFieldData: {
    colorAccent: '#ffffff',
    colorSeekbarLine: '#ffffff',
    showControls: true,
    hasWatermark: false,
    pictureInPicture: false,
    chromecastReceiverId: DEFAULT_CHROMECAST_RECEIVER_APPLICATION_ID,
    advancedOptions: {
      latencyConfig: 'Higher Stability',
      newUi: false,
    },
    source: {
      progressive:
        'https://assets.lcdbackstage.com/8d3fec3d-7d4f-41a2-9661-bcb3ac716668/d3ae96cf-b534-48d7-a514-58f17dfbbaa0.mp4',
    },
  },
  slotConfiguration: {
    between: {maxModules: 100, displayWidth: 12, title: 'Under video controls'},
    atop: {maxModules: 100, displayWidth: 12, title: 'Atop video controls'},
  },
  schema,
  uiSchema,
  instructions,
  category,
})
  .withAnalyticsInstructionMask((instruction) => {
    switch (instruction.type) {
      case 'Video:on-metadata':
        // If `metadata` instructions contain Unicode NULL they fail to be
        // inserted into the postgres analytics database
        return {
          type: instruction.type,
          meta: {
            about: instruction.meta.about,
            data: `${instruction.meta.data}`.includes('\u0000')
              ? null
              : instruction.meta.data,
            type: instruction.meta.type,
          },
        };
    }
    return instruction;
  })
  .withAnimationStates()
  .withStyles()
  .build();

export const defaultFieldData = ComponentDefinition.defaultFieldData;

export type SchemaType = SchemaTypeHelper<typeof ComponentDefinition>;
